import { ChatIcon, CloseIcon } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "cn";
import { useEffect, useRef } from "react";

import {
  CHAT_BUBBLE_BUTTON,
  CHAT_BUBBLE_HEIGHT,
  CHAT_BUBBLE_MARGIN,
  CHAT_BUBBLE_WIDTH,
} from "../lib/limits.ts";

import { ChatThread } from "./ChatThread.tsx";
import type { UseAgentChatResult } from "./use-agent-chat.ts";

interface Props {
  readonly chat: UseAgentChatResult;
  readonly open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Saltar al modo Chat en un mensaje concreto. */
  onOpenInChat: (messageId: string) => void;
}

/**
 * El chat, sin salir de Work.
 *
 * Es EL MISMO chat: el estado llega por props desde el armazón, que es quien
 * tiene el único `useAgentChat()` (§9.2). Escribir aquí y luego cambiar al
 * modo Chat deja al usuario en la misma conversación, con el turno donde
 * estuviera; cerrar esto a mitad de un turno no lo corta, porque el turno no
 * vive en este componente.
 *
 * Cerrada avisa de lo que no puede esperar: una confirmación o una pregunta
 * sin contestar dejan el bucle del agente PARADO (§8.2), y sin el aviso el
 * agente se quedaría esperando a alguien que no sabe que le preguntaron.
 */
export function ChatBubble({ chat, open, onOpenChange, onOpenInChat }: Props) {
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Al cerrar, el foco vuelve de donde salió: si no, se queda en un elemento
  // que acaba de desaparecer y el teclado empieza otra vez desde el principio.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) button.current?.focus();
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;

    /**
     * Escape cierra, esté el foco donde esté.
     *
     * Se respeta lo que ya haya decidido otro: un diálogo abierto encima
     * --los ajustes, la ficha de un participante-- marca el evento al
     * cerrarse, y sin esta comprobación un solo Escape cerraría los dos.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented)
        onOpenChange(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  const waiting = chat.pendingDecisions;

  if (!open) {
    return (
      <Button
        ref={button}
        // El tamaño sale de la configuración y no de una clase: es el mismo
        // número con el que el armazón le reserva el hueco, y con dos fuentes
        // acabarían discrepando.
        style={{ width: CHAT_BUBBLE_BUTTON, height: CHAT_BUBBLE_BUTTON }}
        className="relative rounded-full p-0 shadow-lg"
        aria-label={
          waiting > 0
            ? `Abrir el chat · ${String(waiting)} esperando tu respuesta`
            : "Abrir el chat"
        }
        onClick={() => {
          onOpenChange(true);
        }}
      >
        <ChatIcon className="size-5" />
        {waiting > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 size-5 justify-center rounded-full p-0 text-[0.7rem]"
          >
            {waiting}
          </Badge>
        )}
      </Button>
    );
  }

  return (
    <div
      ref={panel}
      role="dialog"
      aria-label="Chat con el asistente"
      className={cn(
        "flex flex-col overflow-hidden rounded-xl border bg-background shadow-2xl",
        // El alto es el de partida y nunca más de lo que dé la ventana.
        "max-h-full",
      )}
      style={{ width: CHAT_BUBBLE_WIDTH, height: CHAT_BUBBLE_HEIGHT }}
    >
      <header className="flex h-11 shrink-0 items-center gap-2 border-b px-3">
        <ChatIcon className="size-4 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {chat.title ?? "Asistente"}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Cerrar el chat"
          onClick={() => {
            onOpenChange(false);
          }}
        >
          <CloseIcon />
        </Button>
      </header>

      <ChatThread chat={chat} density="compact" onOpenInChat={onOpenInChat} />
    </div>
  );
}

/**
 * El hueco que el armazón le reserva al BOTÓN cerrado, y solo a él.
 *
 * Abajo a la derecha es donde caen la paginación de una tabla y el pie de una
 * pantalla, y ahí el botón no puede quedar encima de algo que hay que pulsar.
 * Así que ese pie se reserva.
 *
 * La ventana abierta NO reserva nada: flota. Reservarle su ancho encogía el
 * contenido 412px de golpe al abrirla --la tabla recalculaba columnas, el
 * Resumen recolocaba sus tarjetas-- y eso es justo lo contrario de flotante.
 * Por eso esto es una constante y no una función de si está abierta: abrir y
 * cerrar no mueve un solo píxel del panel.
 */
export const BUBBLE_RESERVATION = {
  paddingBottom: CHAT_BUBBLE_BUTTON + CHAT_BUBBLE_MARGIN * 2,
} as const;
